import { useGetAvailableIngredientsV2 } from '@shared/api';
import { IngredientPickerItem } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { useState } from 'react';

interface InlineBaseFoodSearchProps {
  selectedItem: IngredientPickerItem | null;
  onSelect: (item: IngredientPickerItem | null) => void;
}

export function InlineBaseFoodSearch({
  selectedItem,
  onSelect,
}: InlineBaseFoodSearchProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const { data: ingredients, isLoading } = useGetAvailableIngredientsV2({
    search: debouncedSearch || undefined,
    include: ['base_foods'],
  });

  return (
    <div className="flex flex-col gap-2">
      {selectedItem && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <Icons.check className="size-3.5 text-teal-600" />
            <Text variant="subtle" className="font-medium text-gray-800">
              {selectedItem.name}
            </Text>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onSelect(null)}
          >
            Change
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-md border bg-gray-00">
        <div className="flex items-center border-b px-3">
          <Icons.search className="mr-2 size-4 shrink-0 opacity-50" />
          <input
            placeholder="Search basic foods..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        {!debouncedSearch ? (
          <div className="py-6 text-center">
            <Text variant="subtle" className="text-muted-foreground">
              Start typing to search basic foods
            </Text>
          </div>
        ) : isLoading ? (
          <div className="py-6 text-center">
            <Text variant="subtle" className="text-muted-foreground">
              Searching...
            </Text>
          </div>
        ) : !ingredients?.length ? (
          <div className="py-6 text-center">
            <Text variant="subtle" className="text-muted-foreground">
              {debouncedSearch.length <= 2
                ? 'Keep typing to see results...'
                : 'No items found.'}
            </Text>
          </div>
        ) : (
          <ul className="max-h-64 overflow-y-auto">
            {ingredients.map((item) => (
              <li
                key={item.uuid}
                onClick={() => onSelect(item)}
                className={cn(
                  'flex min-h-[44px] cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm last:border-0 hover:bg-accent hover:text-accent-foreground',
                  selectedItem?.uuid === item.uuid && 'bg-accent'
                )}
              >
                <Icons.carrot className="size-3 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <Text variant="subtle" className="text-xs">
                    Basic food
                    {item.externalId ? ` | ${item.externalId}` : ''}
                  </Text>
                  <Text className="line-clamp-1">{item.name}</Text>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
